import React, { useCallback, useMemo, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import { useRunAndPersistAdverseMedia } from 'api/bitedb/ai/ai.hooks';

interface OSINTFinding {
  claim: string;
  source: string;
  url: string;
  date?: string;
  confidence?: number;
}

interface OSINTCombinedBlock {
  findings: OSINTFinding[];
  confidence: number; // average confidence across findings
  sources: string[];
  summary?: string;
}

interface EntityInput {
  name: string;
  country?: string;
  address?: string;
  domain?: string;
  aliases?: string[];
}

interface IAIInterface {
  aiSums?: any[];
  aiLoading?: boolean;
  entity?: EntityInput;
  pdfMode?: boolean;
  databaseEntityId?: string;
  databaseId?: string;
  entityId?: string;
  adverseMediaCheckedAt?: string | null;
  adverseMediaInfo?: any;
  websiteQualityInfo?: any;
  hideWebsiteContact?: boolean;
  vertical?: boolean;
}

const AIEvalEntity: React.FC<IAIInterface> = ({
  aiSums = [],
  aiLoading,
  entity,
  pdfMode = false,
  databaseEntityId,
  databaseId,
  entityId,
  adverseMediaInfo,
  websiteQualityInfo,
  hideWebsiteContact = false,
  vertical = false,
}) => {
  /*const toPct = (v?: number) =>
    typeof v === 'number' ? `${(v * 100).toFixed(0)}%` : '';*/
  const first = aiSums?.[0];
  const fallbackName = first?.entity_name || '';

  const contactChecks = websiteQualityInfo?.checks || {};
  const websiteDomain = entity?.domain?.trim() || '';
  const hasWebsiteQualityResult = Boolean(websiteQualityInfo?.checks);
  const contactPresence = [
    {
      label: 'Contact Email on Site',
      value:
        typeof contactChecks.contact_email_found === 'boolean'
          ? contactChecks.contact_email_found
          : null,
    },
    {
      label: 'Contact Name on Site',
      value:
        typeof contactChecks.contact_name_found === 'boolean'
          ? contactChecks.contact_name_found
          : null,
    },
    {
      label: 'Contact Phone on Site',
      value:
        typeof contactChecks.contact_phone_found === 'boolean'
          ? contactChecks.contact_phone_found
          : null,
    },
  ];

  const [osintError, setOsintError] = useState<string | null>(null);
  const [osint, setOsint] = useState<OSINTCombinedBlock | null>(
    adverseMediaInfo ?? null,
  );

  const { mutateAsync: runAdverseMedia, isPending: osintLoading } =
    useRunAndPersistAdverseMedia();

  const entityForOsint: EntityInput = useMemo(() => {
    return {
      name: entity?.name || fallbackName || '',
      country: entity?.country,
      address: entity?.address,
      aliases: entity?.aliases || [],
    };
  }, [entity, fallbackName]);

  const runOsint = useCallback(async () => {
    if (!entityForOsint.name) {
      setOsintError('Missing entity name for adverse findings search.');
      return;
    }
    try {
      setOsintError(null);
      const result = await runAdverseMedia({
        entityName: entityForOsint.name,
        country: entityForOsint.country,
        aliases: entityForOsint.aliases,
        databaseEntityId: databaseEntityId,
        databaseId: databaseId,
        entityId: entityId,
      });
      // result shape: { summary, findings, confidence, sources }
      // map to the OSINTCombinedBlock shape the UI expects
      setOsint((result as any) ?? null);
    } catch (e: any) {
      setOsintError(e?.message || 'Failed to fetch adverse findings.');
      setOsint(null);
    }
  }, [entityForOsint, databaseEntityId, databaseId, entityId, runAdverseMedia]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: pdfMode || vertical ? 'column' : 'row',
        gap: 8,
        height: pdfMode || vertical ? 'auto' : '300px',
      }}
    >
      <div className='screening-ai-container' style={{
        flex: 1,
        minWidth: 0,
      }}>
        {/* Header row */}
        <div className='screening-ai-row' style={{ marginBottom: 10 }}>
          <div className='screening-ai-label-text'>
            <AutoAwesomeIcon sx={{ marginRight: '10px', color: '#4895ef' }} />
            AI Evaluation
          </div>
          <div className='screening-ai-sum-result-text'>
            Date: {first?.screenDate}
          </div>
        </div>

        {/* Top grid: unchanged (Risk + Summary left, Top Matches right) */}
        <div className='ai-grid'>
          {aiLoading ? (
            <div className='ai-grid__loading' style={{ width: '100%' }}>
              <LoadingFeedback size='small' />
            </div>
          ) : (
            aiSums.map((summary, i) => {
              const topMatches = Array.isArray(summary.top_matches)
                ? summary.top_matches
                : [];
              return (
                <div key={i} className='ai-grid__inner'>
                  {/* Left column — Risk level */}
                  <section className='ai-card ai-card--risk'>
                    <div className='screening-ai-sum-group'>
                      <div className='screening-ai-sum-header-text'>
                        Evaluated Risk Level:
                      </div>
                      <div className='screening-ai-sum-result-text'>
                        {summary.risk || '—'}
                      </div>
                    </div>
                  </section>

                  {/* Left column — Summary */}
                  <section className='ai-card ai-card--summary'>
                    <div className='screening-ai-sum-group-summary'>
                      <div
                        className='screening-ai-sum-header-text'
                        style={{ textDecoration: 'underline' }}
                      >
                        Risk Evaluation Summary
                      </div>
                      <div className='screening-ai-sum-result-text'>
                        {summary.explanation || '—'}
                      </div>
                    </div>
                  </section>

                  {/* Right column — Top Matches */}
                  <section className='ai-card ai-card--top'>
                    <div className='screening-ai-sum-header-text ai-section-title'>
                      Top Matches
                    </div>
                    <div className='screening-ai-sum-result-text'>
                      {topMatches.length === 0 ? (
                        <span>{'See Below'}</span>
                      ) : (
                        <ol style={{ margin: 0, paddingLeft: 18 }}>
                          {topMatches.map((m: any, idx: number) => (
                            <li
                              key={`${m.id || m.name}-${idx}`}
                              style={{ margin: '2px 0' }}
                            >
                              <strong>{m.name}</strong>{' '}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </section>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className='screening-ai-container'
      style={{ flex: 1, minWidth: 0 }}
      >
        <section className='ai-card ai-card--osint'>
          <div className='screening-ai-label-text'>
            Adverse Findings (on-demand)
          </div>
          {!osint?.summary && (
            <p
              className='screening-ai-sum-result-text'
              style={{ margin: '2px 0 8px', opacity: 0.85 }}
            >
              Runs a targeted open-source search for adverse information tied to
              this entity — including sanctions, enforcement actions,
              litigation, and ownership/control red flags. Results are
              summarized with up to three reputable sources and are intended to
              complement the risk evaluation above.
            </p>
          )}

          {/* Controls */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: 8,
              justifyContent: 'end',
            }}
          >
            <div
              className='screening-ai-sum-result-text'
              style={{ opacity: 0.8 }}
            >
              {entityForOsint.name
                ? `Entity: ${entityForOsint.name}${
                    entityForOsint.country ? `, ${entityForOsint.country}` : ''
                  }`
                : 'Provide an entity name to run check.'}
            </div>
            <button
              type='button'
              className='btn btn-primary'
              onClick={runOsint}
              disabled={osintLoading || !entityForOsint.name}
              style={{ padding: '6px 12px', borderRadius: 8 }}
            >
              {osint ? 'Re-run Check' : 'Check Adverse Media'}
            </button>
          </div>

          {/* Loading / Error */}
          {osintLoading && (
            <div style={{ marginTop: 6 }}>
              <LoadingFeedback size='small' />
            </div>
          )}
          {osintError && (
            <div
              className='screening-ai-sum-result-text'
              style={{ color: '#b00020', marginTop: 6 }}
            >
              {osintError}
            </div>
          )}

          {/* Results */}
          {!osintLoading && osint && (
            <div style={{ display: 'grid', gap: 8 }}>
              {/* Optional meta row */}
              {/* <div className='screening-ai-sum-result-text' style={{ opacity: 0.8 }}>
              Average confidence: {toPct(osint.confidence)}
              </div> */}

              {/* AI summary */}
              {osint.summary ? (
                <div
                  className='screening-ai-sum-result-text'
                  style={{ margin: '2px 0 6px' }}
                >
                  {osint.summary}
                </div>
              ) : null}

              {/* Links */}
              {osint.findings?.length ? (
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {osint.findings.slice(0, 5).map((f, idx) => (
                    <li key={`os-${idx}`} style={{ margin: '2px 0' }}>
                      <a href={f.url} target='_blank' rel='noopener noreferrer'>
                        {f.claim || f.source}
                      </a>
                      {f.date ? (
                        <span style={{ opacity: 0.8 }}> — {f.date}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className='screening-ai-sum-result-text'>No findings.</div>
              )}
            </div>
          )}
        </section>
      </div>
      {!hideWebsiteContact && <div
        className='screening-ai-container'
        style={{ flex: 1, minWidth: 0 }}
      >
        <section className='ai-card ai-card--osint'>
          <div className='screening-ai-label-text'>Website Contact Presence</div>
          {!websiteDomain ? (
            <p
              className='screening-ai-sum-result-text'
              style={{ margin: '2px 0 12px', opacity: 0.85 }}
            >
              No domain was provided, so website contact presence could not be
              checked.
            </p>
          ) : !hasWebsiteQualityResult ? (
            <p
              className='screening-ai-sum-result-text'
              style={{ margin: '2px 0 12px', opacity: 0.85 }}
            >
              Website contact presence has not been checked yet.
            </p>
          ) : (
            <>
              <p
                className='screening-ai-sum-result-text'
                style={{ margin: '2px 0 12px', opacity: 0.85 }}
              >
                Shows whether {websiteDomain} references the contact details
                used for verification.
              </p>
              <div style={{ display: 'grid', gap: 10 }}>
                {contactPresence.map((item) => {
                  const hasValue = typeof item.value === 'boolean';
                  return (
                    <div
                      key={item.label}
                      className='ai-card'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        padding: '10px 12px',
                      }}
                    >
                      <div className='screening-ai-sum-header-text'>
                        {item.label}
                      </div>
                      <div
                        className='screening-ai-sum-result-text'
                        style={{
                          color: hasValue
                            ? item.value
                              ? '#12B76A'
                              : '#F04438'
                            : 'var(--text-secondary)',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {hasValue ? (item.value ? 'Yes' : 'No') : 'Not checked'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>}
    </div>
  );
};

export default AIEvalEntity;
